import { TFunction } from 'i18next';

import { listNames } from 'companySettings/components/PartFinderProductTypes/utils';
import { AttributeStatus } from 'companySettings/enums';
import {
  IPartFinderFamily,
  IPartFinderFamilyChanges,
  IPartFinderFamilyRegrouped,
  IPartFinderFamilySync,
} from 'companySettings/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';

// A draft that was never published and has no parts yet is still being set up in the wizard.
export const opensInWizard = (family: IPartFinderFamily) =>
  AttributeStatus.DRAFT === family.status && !family.partsCount;

// An attribute that went into a group, out of one, or from one to another.
const formatRegrouped =
  (t: TFunction) =>
  ({ name, from, to }: IPartFinderFamilyRegrouped) => {
    if (!from) {
      return t('labelPartFinderFamilyAttributeGrouped', { name, to });
    }

    return to
      ? t('labelPartFinderFamilyAttributeRegrouped', { name, from, to })
      : t('labelPartFinderFamilyAttributeUngrouped', { name, from });
  };

// A value list's or product type's release a sync took into the family.
const formatSync =
  (t: TFunction) =>
  ({ type, name, number }: IPartFinderFamilySync) =>
    t(
      'list' === type
        ? 'labelPartFinderFamilySyncedList'
        : 'labelPartFinderFamilySyncedProductType',
      {
        name,
        number,
      }
    );

// What a save or a version changed on a family, a line each, as a product type's history has it:
// its details, its attributes, then its parts. An un-publish and a publish that put the family
// back as it was are rows of their own.
export const getFamilyChangeLines = (changes: IPartFinderFamilyChanges, t: TFunction): string[] => {
  const attributes = changes?.attributes;
  const parts = changes?.parts;

  const partLines = [
    parts?.added?.length > 0 &&
      t('labelPartFinderFamilyPartsAdded', {
        count: parts.added.length,
        names: listNames(parts.added),
      }),
    parts?.removed?.length > 0 &&
      t('labelPartFinderFamilyPartsRemoved', {
        count: parts.removed.length,
        names: listNames(parts.removed),
      }),
    parts?.valuesChanged > 0 &&
      t('labelPartFinderFamilyPartValuesChanged', { count: parts.valuesChanged }),
  ];

  return (
    changes?.created
      ? [
          t('labelPartFinderProductTypeCreated', { count: attributes?.added?.length ?? 0 }),
          ...partLines,
        ]
      : [
          changes?.unpublished && t('labelPartFinderFamilyUnpublished'),
          changes?.republished && t('labelPartFinderFamilyRepublished'),
          ...(changes?.sync ?? []).map(formatSync(t)),
          changes?.name && t('labelPartFinderAttributeRenamed', changes.name),
          changes?.productType &&
            t('labelPartFinderFamilyProductTypeChanged', {
              from: changes.productType.from?.name ?? DEFAULT_PLACEHOLDER,
              to: changes.productType.to?.name ?? DEFAULT_PLACEHOLDER,
            }),
          changes?.category &&
            t('labelPartFinderFamilyCategoryChanged', {
              from: changes.category.from?.name ?? DEFAULT_PLACEHOLDER,
              to: changes.category.to?.name ?? DEFAULT_PLACEHOLDER,
            }),
          attributes?.added?.length > 0 &&
            t('labelPartFinderProductTypeAttributesAdded', {
              count: attributes.added.length,
              names: listNames(attributes.added.map((attribute) => attribute.name)),
            }),
          attributes?.removed?.length > 0 &&
            t('labelPartFinderProductTypeAttributesRemoved', {
              count: attributes.removed.length,
              names: listNames(attributes.removed.map((attribute) => attribute.name)),
            }),
          ...(attributes?.renamed ?? []).map((renamed) =>
            t('labelPartFinderProductTypeAttributeRenamed', renamed)
          ),
          attributes?.changed?.length > 0 &&
            t('labelPartFinderProductTypeAttributesChanged', {
              count: attributes.changed.length,
              names: listNames(attributes.changed.map((attribute) => attribute.name)),
            }),
          attributes?.reordered && t('labelPartFinderProductTypeAttributesReordered'),
          attributes?.used?.length > 0 &&
            t('labelPartFinderFamilyAttributesUsed', {
              count: attributes.used.length,
              names: listNames(attributes.used.map((attribute) => attribute.name)),
            }),
          ...(attributes?.regrouped ?? []).map(formatRegrouped(t)),
          attributes?.unused?.length > 0 &&
            t('labelPartFinderFamilyAttributesUnused', {
              count: attributes.unused.length,
              names: listNames(attributes.unused.map((attribute) => attribute.name)),
            }),
          ...partLines,
        ]
  ).filter(Boolean);
};

// The same in one line for the history. A save that changed nothing only published it.
export const formatFamilyChanges = (
  changes: IPartFinderFamilyChanges,
  t: TFunction,
  isPublish = false
) => {
  const lines = getFamilyChangeLines(changes, t);

  if (lines.length) {
    return lines.join(' · ');
  }

  return isPublish ? t('labelPartFinderAttributePublished') : t('labelNoChanges');
};
